import React, { useState, useEffect } from 'react';
import { taskTypeService, CreateTaskTypeRequest } from '../services/taskTypeService';
import { useTaskStore } from '../../../stores/taskStore';
import { useAuthStore } from '../../../stores/authStore';
import { useWorkspaceStore } from '../../../stores/workspaceStore';
import { useDialog } from '../../../hooks/useDialog';
import { userService } from '../../../services/userService';
import type { TaskType } from '../../../types';
import { getUserDisplayName } from '../../../utils/userUtils';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';

const DEFAULT_COLORS = [
  '#EF4444', // Red
  '#F97316', // Orange
  '#EAB308', // Yellow
  '#22C55E', // Green
  '#06B6D4', // Cyan
  '#3B82F6', // Blue
  '#8B5CF6', // Violet
  '#EC4899', // Pink
  '#6B7280', // Gray
  '#059669', // Emerald
  '#DC2626', // Red-600
  '#7C3AED', // Purple
];

interface TaskTypeManagerProps {
  isOpen: boolean;
  onClose: () => void;
}

export const TaskTypeManager: React.FC<TaskTypeManagerProps> = ({ isOpen, onClose }) => {
  const taskTypes = useTaskStore(state => state.taskTypes);
  const { user, updateUser } = useAuthStore();
  const { currentWorkspace } = useWorkspaceStore();
  const { confirm, alert } = useDialog();
  const [activeTab, setActiveTab] = useState<'taskTypes' | 'userSettings'>('taskTypes');
  
  // 任务类型相关状态
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newTaskType, setNewTaskType] = useState({ name: '', color: DEFAULT_COLORS[0] });
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  
  // 用户设置相关状态
  const [userSettings, setUserSettings] = useState({
    nickname: user?.nickname || '',
    email: user?.email || ''
  });
  const [userLoading, setUserLoading] = useState(false);

  // 当用户信息变化时同步到userSettings
  useEffect(() => {
    if (user && isOpen) {
      setUserSettings({
        nickname: user.nickname || '',
        email: user.email || ''
      });
    }
  }, [user, isOpen]);

  useEffect(() => {
    if (isOpen) {
      const loadData = async () => {
        setInitialLoading(true);
        setLoadError(null);
        try {
          // 根据当前工作空间加载任务类型
          await useTaskStore.getState().loadTaskTypes(
            currentWorkspace.type,
            currentWorkspace.teamId
          );
        } catch (error: any) {
          console.error('加载任务类型失败:', error);
          setLoadError(error.message || '加载任务类型失败');
        } finally {
          setInitialLoading(false);
        }
      };
      loadData();
    }
  }, [isOpen, currentWorkspace]); // 当工作空间变化时也重新加载

  const handleCreate = async () => {
    if (!newTaskType.name.trim()) return;

    setLoading(true);
    try {
      await taskTypeService.createTaskType(
        {
          name: newTaskType.name.trim(),
          color: newTaskType.color
        },
        currentWorkspace.type,
        currentWorkspace.teamId
      );
      setNewTaskType({ name: '', color: DEFAULT_COLORS[0] });
      setIsCreating(false);
      // 重新加载当前工作空间的任务类型
      await useTaskStore.getState().loadTaskTypes(
        currentWorkspace.type,
        currentWorkspace.teamId
      );
    } catch (error) {
      console.error('创建任务类型失败:', error);
      await alert({
        title: '创建失败',
        message: '创建任务类型失败',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (id: string, data: { name: string; color: string }) => {
    if (!data.name.trim()) return;

    setLoading(true);
    try {
      await taskTypeService.updateTaskType(id, data);
      setEditingId(null);
      await useTaskStore.getState().loadTaskTypes(
        currentWorkspace.type,
        currentWorkspace.teamId
      );
    } catch (error) {
      console.error('更新任务类型失败:', error);
      await alert({
        title: '更新失败',
        message: '更新任务类型失败',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    const confirmed = await confirm({
      title: '删除任务类型',
      message: `确定要删除任务类型"${name}"吗？\n删除后，关联的任务将变为未分类。`,
      confirmText: '删除',
      cancelText: '取消',
      confirmVariant: 'danger'
    });

    if (!confirmed) {
      return;
    }

    setLoading(true);
    try {
      await taskTypeService.deleteTaskType(id);
      await useTaskStore.getState().loadTaskTypes(
        currentWorkspace.type,
        currentWorkspace.teamId
      );
    } catch (error) {
      console.error('删除任务类型失败:', error);
      await alert({
        title: '删除失败',
        message: '删除任务类型失败',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // 分类建议模板
  const taskTypeTemplates = {
    team: {
      name: '团队功能分类',
      description: '适合按团队职能划分的组织结构',
      types: [
        { name: '产品', color: '#3B82F6' },
        { name: '设计', color: '#8B5CF6' },
        { name: '技术', color: '#22C55E' },
        { name: '运营', color: '#F97316' },
        { name: '商务', color: '#EAB308' },
      ]
    },
    project: {
      name: '项目阶段分类',
      description: '适合按项目开发阶段划分',
      types: [
        { name: '需求分析', color: '#3B82F6' },
        { name: '设计', color: '#8B5CF6' },
        { name: '开发', color: '#22C55E' },
        { name: '测试', color: '#F97316' },
        { name: '部署', color: '#EAB308' },
      ]
    },
    general: {
      name: '通用任务分类',
      description: '适合一般的任务管理场景',
      types: [
        { name: '开发', color: '#3B82F6' },
        { name: '设计', color: '#8B5CF6' },
        { name: '测试', color: '#22C55E' },
        { name: '文档', color: '#F97316' },
        { name: '会议', color: '#EAB308' },
      ]
    }
  };

  const createDefaultTaskTypes = async (templateKey: keyof typeof taskTypeTemplates = 'general') => {
    const template = taskTypeTemplates[templateKey];
    const defaultTypes = template.types;

    setLoading(true);
    try {
      for (const type of defaultTypes) {
        await taskTypeService.createTaskType(
          type,
          currentWorkspace.type,
          currentWorkspace.teamId
        );
      }
      await useTaskStore.getState().loadTaskTypes(
        currentWorkspace.type,
        currentWorkspace.teamId
      );
      await alert({
        title: '创建成功',
        message: '默认任务类型创建成功！',
        type: 'success'
      });
    } catch (error) {
      console.error('创建默认任务类型失败:', error);
      await alert({
        title: '创建失败',
        message: '创建默认任务类型失败',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // 保存用户设置
  const handleSaveUserSettings = async () => {
    setUserLoading(true);
    try {
      const updatedUser = await userService.updateProfile({
        nickname: userSettings.nickname.trim() || undefined,
        email: userSettings.email.trim() || undefined
      });
      
      // 更新本地用户状态
      updateUser(updatedUser);
      // 保存成功后关闭窗口
      onClose();
    } catch (error) {
      console.error('更新用户信息失败:', error);
      await alert({
        title: '更新失败',
        message: '更新用户信息失败',
        type: 'error'
      });
    } finally {
      setUserLoading(false);
    }
  };

  // 重置用户设置为当前值
  const resetUserSettings = () => {
    setUserSettings({
      nickname: user?.nickname || '',
      email: user?.email || ''
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* 头部 */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-4">
            <h2 className="text-lg font-semibold text-gray-900">设置</h2>
            {/* 当前工作空间指示器 */}
            <div className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded flex items-center gap-1">
              {currentWorkspace.type === 'personal' ? (
                <>
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  个人空间
                </>
              ) : (
                <>
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  {currentWorkspace.teamName || '团队空间'}
                </>
              )}
            </div>
            {/* 标签页 */}
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setActiveTab('taskTypes')}
                className={`px-3 py-1 text-sm rounded-md transition-colors ${
                  activeTab === 'taskTypes'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                任务类型
              </button>
              <button
                onClick={() => setActiveTab('userSettings')}
                className={`px-3 py-1 text-sm rounded-md transition-colors ${
                  activeTab === 'userSettings'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                个人设置
              </button>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* 内容 */}
        <div className="p-4 flex-1 overflow-y-auto">
          {activeTab === 'taskTypes' ? (
            // 任务类型管理标签页
            <>
              {/* 初始加载状态 */}
              {initialLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-gray-600">加载任务类型中...</span>
                  </div>
                </div>
              ) : loadError ? (
                <div className="text-center py-8">
                  <div className="text-red-500 mb-4">
                    <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    <p className="text-lg text-red-600">加载失败</p>
                    <p className="text-sm text-red-400 mb-4">{loadError}</p>
                    <button
                      onClick={() => {
                        setLoadError(null);
                        setInitialLoading(true);
                        useTaskStore.getState().loadTaskTypes().catch(error => {
                          setLoadError(error.message || '加载任务类型失败');
                        }).finally(() => {
                          setInitialLoading(false);
                        });
                      }}
                      className="px-4 py-2 bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors"
                    >
                      重试
                    </button>
                  </div>
                </div>
              ) : (
            <>
              {/* 现有任务类型列表 */}
              {taskTypes.length > 0 ? (
                <div className="space-y-2 mb-4">
                  {taskTypes.map((taskType) => (
                    <TaskTypeItem
                      key={taskType.id}
                      taskType={taskType}
                      isEditing={editingId === taskType.id}
                      onEdit={() => setEditingId(taskType.id)}
                      onCancelEdit={() => setEditingId(null)}
                      onSave={(data) => handleUpdate(taskType.id, data)}
                      onDelete={() => handleDelete(taskType.id, taskType.name)}
                      disabled={loading}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="text-gray-400 mb-6">
                    <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                    <p className="text-lg text-gray-500">暂无任务类型</p>
                    <p className="text-sm text-gray-400">选择一个分类模板快速开始</p>
                  </div>

                  {/* 分类模板选择 */}
                  <div className="space-y-2 mb-4">
                    {Object.entries(taskTypeTemplates).map(([key, template]) => (
                      <div
                        key={key}
                        className="bg-gray-50 rounded-lg p-3 text-left cursor-pointer hover:bg-blue-50 transition-colors border border-transparent hover:border-blue-200"
                        onClick={() => createDefaultTaskTypes(key as keyof typeof taskTypeTemplates)}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium text-gray-900">{template.name}</h4>
                            <p className="text-sm text-gray-500 mt-1">{template.description}</p>
                            <div className="flex items-center gap-2 mt-2">
                              {template.types.map((type, index) => (
                                <div key={index} className="flex items-center gap-1">
                                  <div
                                    className="w-3 h-3 rounded-full"
                                    style={{ backgroundColor: type.color }}
                                  />
                                  <span className="text-xs text-gray-600">{type.name}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* 创建新类型表单 */}
          {isCreating ? (
            <CreateTaskTypeForm
              data={newTaskType}
              onChange={setNewTaskType}
              onSave={handleCreate}
              onCancel={() => {
                setIsCreating(false);
                setNewTaskType({ name: '', color: DEFAULT_COLORS[0] });
              }}
              disabled={loading}
            />
          ) : (
            <button
              onClick={() => setIsCreating(true)}
              className="w-full p-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-blue-300 hover:text-blue-600 transition-colors"
            >
              + 添加新的任务类型
            </button>
          )}
            </>
          ) : (
            // 用户设置标签页
            <div className="space-y-6">
              {/* 当前用户信息展示 */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-medium">
                    {user?.name?.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {user ? getUserDisplayName(user) : '未知用户'}
                    </p>
                    <p className="text-sm text-gray-500">@{user?.name}</p>
                  </div>
                </div>
              </div>

              {/* 用户设置表单 */}
              <div className="space-y-6">
                <Input
                  label="昵称"
                  type="text"
                  value={userSettings.nickname}
                  onChange={(e) => setUserSettings({ ...userSettings, nickname: e.target.value })}
                  placeholder={`默认：${user?.name?.charAt(0).toUpperCase()}${user?.name?.slice(1).toLowerCase()}`}
                  disabled={userLoading}
                  helperText="显示给其他用户的名称"
                />

                <Input
                  label="邮箱"
                  type="email"
                  value={userSettings.email}
                  onChange={(e) => setUserSettings({ ...userSettings, email: e.target.value })}
                  placeholder="输入你的邮箱地址..."
                  disabled={userLoading}
                  helperText="用于接收通知和找回密码"
                />

                {/* 操作按钮 */}
                <div className="flex justify-end gap-3 pt-6 border-t border-gray-100">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={resetUserSettings}
                    disabled={userLoading}
                  >
                    重置
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleSaveUserSettings}
                    loading={userLoading}
                    disabled={userLoading}
                  >
                    保存设置
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// 任务类型项组件
interface TaskTypeItemProps {
  taskType: TaskType;
  isEditing: boolean;
  onEdit: () => void;
  onCancelEdit: () => void;
  onSave: (data: { name: string; color: string }) => void;
  onDelete: () => void;
  disabled: boolean;
}

const TaskTypeItem: React.FC<TaskTypeItemProps> = ({
  taskType,
  isEditing,
  onEdit,
  onCancelEdit,
  onSave,
  onDelete,
  disabled
}) => {
  const [editData, setEditData] = useState({ name: taskType.name, color: taskType.color || DEFAULT_COLORS[0] });

  useEffect(() => {
    if (isEditing) {
      setEditData({ name: taskType.name, color: taskType.color || DEFAULT_COLORS[0] });
    }
  }, [isEditing, taskType]);

  if (isEditing) {
    return (
      <CreateTaskTypeForm
        data={editData}
        onChange={setEditData}
        onSave={() => onSave(editData)}
        onCancel={onCancelEdit}
        disabled={disabled}
        isEditing
      />
    );
  }

  return (
    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
      <div className="flex items-center gap-2">
        <div
          className="w-4 h-4 rounded-full shadow-sm"
          style={{ backgroundColor: taskType.color || DEFAULT_COLORS[0] }}
        />
        <span className="text-sm font-medium text-gray-900">{taskType.name}</span>
      </div>
      <div className="flex items-center gap-1.5">
        <Button
          variant="secondary"
          size="sm"
          onClick={onEdit}
          disabled={disabled}
          className="text-xs"
        >
          编辑
        </Button>
        <Button
          variant="danger"
          size="sm"
          onClick={onDelete}
          disabled={disabled}
          className="text-xs"
        >
          删除
        </Button>
      </div>
    </div>
  );
};

// 创建/编辑表单组件
interface CreateTaskTypeFormProps {
  data: { name: string; color: string };
  onChange: (data: { name: string; color: string }) => void;
  onSave: () => void;
  onCancel: () => void;
  disabled: boolean;
  isEditing?: boolean;
}

const CreateTaskTypeForm: React.FC<CreateTaskTypeFormProps> = ({
  data,
  onChange,
  onSave,
  onCancel,
  disabled,
  isEditing = false
}) => {
  return (
    <div className="p-4 border border-gray-200 rounded-lg bg-white shadow-sm">
      <div className="space-y-4">
        <Input
          label="类型名称"
          type="text"
          value={data.name}
          onChange={(e) => onChange({ ...data, name: e.target.value })}
          placeholder="输入任务类型名称..."
          disabled={disabled}
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            颜色
          </label>
          <div className="flex flex-wrap gap-2">
            {DEFAULT_COLORS.map((color) => (
              <button
                key={color}
                type="button"
                onClick={() => onChange({ ...data, color })}
                disabled={disabled}
                className={`w-6 h-6 rounded-full border-2 transition-all hover:scale-105 ${
                  data.color === color
                    ? 'border-gray-500 ring-2 ring-blue-200'
                    : 'border-gray-200 hover:border-gray-400'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
                style={{ backgroundColor: color }}
              />
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-gray-100">
          <Button
            variant="secondary"
            size="sm"
            onClick={onCancel}
            disabled={disabled}
          >
            取消
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={onSave}
            disabled={disabled || !data.name.trim()}
          >
            {isEditing ? '保存' : '创建'}
          </Button>
        </div>
      </div>
    </div>
  );
};